/**
 * Client-side event attribution stamping + category helpers.
 * Mirrors backend global/eventAttribution.js for consistent KPI scoping.
 */

import {
  isMagicFurnishStaffEmail,
  isPartnerAgentEmail,
} from './sessionAudience';

export const ATTRIBUTION_VERSION = 2;

const SUPPLY_EVENTS = new Set([
  'floorplan_uploaded',
  'gallery_ready',
  'upload_failed',
]);

const DEMAND_EVENTS = new Set([
  'webview_opened',
  'shuffle_room',
  'inquiry_intent',
  'inquiry_modal_opened',
  'inquiry_submitted',
  'editor_opened',
  'editor_closed',
  'add_items_manually',
  'link_shared',
  'link_copied',
  'download_layout',
  'auth_wall_triggered',
]);

const ACCOUNT_EVENTS = new Set(['account_created']);

const looksLikeEmail = (s) =>
  typeof s === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

export const METRIC_SCOPES = {
  supply: 'supply',
  demand: 'demand',
  account: 'account',
};

export function getEventCategory(eventName) {
  if (!eventName) return null;
  const ev = String(eventName);
  if (SUPPLY_EVENTS.has(ev)) return METRIC_SCOPES.supply;
  if (DEMAND_EVENTS.has(ev)) return METRIC_SCOPES.demand;
  if (ACCOUNT_EVENTS.has(ev)) return METRIC_SCOPES.account;
  return null;
}

export function rowEventCategory(row) {
  const fromDetail = row?.detail?.eventCategory;
  if (fromDetail) return fromDetail;
  return getEventCategory(row?.meta?.event);
}

function extractActorEmail(meta = {}, detail = {}) {
  const candidates = [
    meta?.email,
    detail?.email,
    detail?.userEmail,
    detail?.inquirerEmail,
    meta?.user,
  ];
  for (const raw of candidates) {
    if (looksLikeEmail(raw)) return raw.trim().toLowerCase();
  }
  return null;
}

export function inferActorRole(meta = {}, detail = {}) {
  if (detail?.actorRole) return detail.actorRole;

  const email = extractActorEmail(meta, detail);
  const metaUser = meta?.user;
  const isGuest =
    !email && (!metaUser || metaUser === 'guest' || metaUser === 'anonymous');

  if (isGuest) return 'guest';
  if (detail?.audience === 'internal') return 'mf_staff';
  if (detail?.audience === 'agent') return 'agent';
  if (isMagicFurnishStaffEmail(email)) return 'mf_staff';
  if (isPartnerAgentEmail(email)) return 'agent';
  return 'buyer';
}

/**
 * Stamp additive attribution on the detail payload before POST /analytics.
 */
export function buildClientAttributionDetail(eventName, payload = {}, options = {}) {
  const detail = payload && typeof payload === 'object' ? { ...payload } : {};
  const metaUser = options.userEmail || null;

  if (!detail.attributionVersion) {
    detail.attributionVersion = ATTRIBUTION_VERSION;
  }
  if (!detail.eventCategory && eventName) {
    detail.eventCategory = getEventCategory(eventName);
  }
  if (!detail.actorRole) {
    detail.actorRole = inferActorRole(
      { user: metaUser || detail.email || 'guest' },
      detail
    );
  }
  if (!detail.uploaderEmail && looksLikeEmail(metaUser)) {
    detail.uploaderEmail = metaUser.trim().toLowerCase();
  }
  return detail;
}

export function getMetricScopeForEvent(eventKey) {
  if (!eventKey) return METRIC_SCOPES.demand;
  const cat = getEventCategory(eventKey);
  if (cat) return cat;
  return METRIC_SCOPES.demand;
}
